import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Box, Download, ImageUp, Loader2, RefreshCw, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { apiErrorMessage, useDemoSitesApi } from '../../../common/hooks/use-demo-sites-api.hook';
import { ModelPreview } from './model-preview';

type Unit = 'ft' | 'cm';

const CM_PER_FT = 30.48;
const CM_PER_IN = 2.54;
/** The sizes rugs are sold in, in feet (width × length). */
const PRESETS_FT: [number, number][] = [[2, 3], [3, 5], [4, 6], [5, 8], [6, 9], [8, 10], [9, 12], [10, 14]];
/** The sides the API accepts, in cm. */
const SIDE_CM = { min: 20, max: 2000 };
/** Pile heights the API accepts, in cm (a flat-weave to a deep shag), and the default: the 8 mm the visualizer draws rugs at. */
const THICKNESS_CM = { min: 0.2, max: 10, default: 0.8 };
/** Usual pile heights, typed as they are in each unit. */
const THICKNESS_PRESETS: Record<Unit, number[]> = { cm: [0.5, 0.8, 1.5, 2, 3], ft: [0.25, 0.3, 0.5, 0.75, 1.25] };

/** A thickness is typed in inches when the sizes are in feet, else in cm. */
const thicknessToCm = (value: string, unit: Unit): number => {
  const n = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return NaN;
  return Math.round((unit === 'ft' ? n * CM_PER_IN : n) * 100) / 100;
};
const thicknessFromCm = (cm: number, unit: Unit): string =>
  !Number.isFinite(cm) ? '' : String(Math.round((unit === 'ft' ? cm / CM_PER_IN : cm) * 100) / 100);

const toCm = (value: string, unit: Unit): number => {
  const n = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return NaN;
  return Math.round((unit === 'ft' ? n * CM_PER_FT : n) * 100) / 100;
};
const fromCm = (cm: number, unit: Unit): string =>
  !Number.isFinite(cm) ? '' : String(Math.round((unit === 'ft' ? cm / CM_PER_FT : cm) * 100) / 100);

/** A file name from the photo's: "caesar.webp" at 5 × 8 ft → "caesar-152x244cm.glb". */
const glbName = (photo: File | null, widthCm: number, lengthCm: number): string => {
  const base = (photo?.name || 'rug').replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'rug';
  return `${base}-${Math.round(widthCm)}x${Math.round(lengthCm)}cm.glb`;
};

/**
 * The 3D generator's rug: a rug's top-down photo and its size in, its 3D model
 * (.glb) out, to add a rug by hand. Made by the API the way a demo's rug is: the
 * photo's plain border taken off, rounded corners squared, the sides in the rug's
 * own colour.
 */
export const RugGenerator: React.FC = () => {
  const { t } = useTranslation();
  const api = useDemoSitesApi();

  const [photo, setPhoto] = useState<File | null>(null);
  const [unit, setUnit] = useState<Unit>('ft');
  const [width, setWidth] = useState('5');
  const [length, setLength] = useState('8');
  // Inches while the size is in feet: about the default 8 mm.
  const [thickness, setThickness] = useState('0.3');
  const [making, setMaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState<{ url: string; name: string; bytes: number } | null>(null);

  const photoUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);
  useEffect(() => () => { if (model) URL.revokeObjectURL(model.url); }, [model]);

  const widthCm = toCm(width, unit);
  const lengthCm = toCm(length, unit);
  const sizeOk = [widthCm, lengthCm].every((v) => v >= SIDE_CM.min && v <= SIDE_CM.max);
  const thicknessCm = thicknessToCm(thickness, unit);
  const thicknessOk = thicknessCm >= THICKNESS_CM.min && thicknessCm <= THICKNESS_CM.max;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.avif'] },
    multiple: false,
    onDrop: (files) => {
      if (!files[0]) return;
      setPhoto(files[0]);
      setModel(null);
      setError(null);
    },
  });

  const switchUnit = (next: Unit) => {
    if (!next || next === unit) return;
    setWidth(fromCm(widthCm, next));
    setLength(fromCm(lengthCm, next));
    setThickness(thicknessFromCm(thicknessCm, next));
    setUnit(next);
  };

  const preset = ([w, l]: [number, number]) => {
    setWidth(fromCm(w * CM_PER_FT, unit));
    setLength(fromCm(l * CM_PER_FT, unit));
    setModel(null);
  };

  const make = async () => {
    if (!photo || !sizeOk || !thicknessOk) return;
    setMaking(true);
    setError(null);
    try {
      const glb = await api.rugModel(photo, widthCm, lengthCm, thicknessCm);
      const url = URL.createObjectURL(new Blob([glb], { type: 'model/gltf-binary' }));
      setModel({ url, name: glbName(photo, widthCm, lengthCm), bytes: glb.byteLength });
    } catch (e) {
      setError((await apiErrorMessage(e)) || t('SUPER_ADMIN.RUG_GENERATOR.FAILED'));
    } finally {
      setMaking(false);
    }
  };

  return (
      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <Card className="flex flex-col gap-6 p-6">
          {/* The photo */}
          <Field>
            <FieldLabel>{t('SUPER_ADMIN.RUG_GENERATOR.PHOTO')}</FieldLabel>
            {photo && photoUrl ? (
              <div className="relative overflow-hidden rounded-lg border bg-muted">
                <img src={photoUrl} alt={photo.name} className="mx-auto block max-h-72 object-contain" />
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute end-2 top-2 size-8 bg-background"
                  aria-label={t('SUPER_ADMIN.RUG_GENERATOR.REMOVE_PHOTO')}
                  onClick={() => { setPhoto(null); setModel(null); }}
                >
                  <X />
                </Button>
              </div>
            ) : (
              <div
                {...getRootProps()}
                data-testid="rug-photo-drop"
                className={cn(
                  'flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground transition-colors',
                  isDragActive && 'border-foreground bg-muted',
                )}
              >
                <input {...getInputProps()} data-testid="rug-photo-input" />
                <ImageUp className="size-6" />
                <span>{t('SUPER_ADMIN.RUG_GENERATOR.DROP')}</span>
              </div>
            )}
            <FieldDescription>{t('SUPER_ADMIN.RUG_GENERATOR.PHOTO_HINT')}</FieldDescription>
          </Field>

          {/* The size */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('SUPER_ADMIN.RUG_GENERATOR.SIZE')}</span>
              <ToggleGroup type="single" variant="outline" size="sm" value={unit} onValueChange={(v) => switchUnit(v as Unit)}>
                <ToggleGroupItem value="ft">{t('SUPER_ADMIN.RUG_GENERATOR.FEET')}</ToggleGroupItem>
                <ToggleGroupItem value="cm">cm</ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS_FT.map(([w, l]) => (
                <Button key={`${w}x${l}`} variant="outline" size="sm" onClick={() => preset([w, l])}>
                  {w}′ × {l}′
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="rug-width">{t('SUPER_ADMIN.RUG_GENERATOR.WIDTH', { unit: unit === 'ft' ? 'ft' : 'cm' })}</FieldLabel>
                <Input id="rug-width" inputMode="decimal" value={width} onChange={(e) => { setWidth(e.target.value); setModel(null); }} />
              </Field>
              <Field>
                <FieldLabel htmlFor="rug-length">{t('SUPER_ADMIN.RUG_GENERATOR.LENGTH', { unit: unit === 'ft' ? 'ft' : 'cm' })}</FieldLabel>
                <Input id="rug-length" inputMode="decimal" value={length} onChange={(e) => { setLength(e.target.value); setModel(null); }} />
              </Field>
            </div>
            <p className={cn('text-xs', sizeOk ? 'text-muted-foreground' : 'text-destructive')} data-testid="rug-size-cm">
              {sizeOk
                ? t('SUPER_ADMIN.RUG_GENERATOR.SIZE_CM', { width: Math.round(widthCm * 10) / 10, length: Math.round(lengthCm * 10) / 10 })
                : t('SUPER_ADMIN.RUG_GENERATOR.SIZE_INVALID', { min: SIDE_CM.min, max: SIDE_CM.max })}
            </p>
          </div>

          {/* The thickness (pile height) */}
          <div className="flex flex-col gap-3">
            <Field>
              <FieldLabel htmlFor="rug-thickness">
                {t('SUPER_ADMIN.RUG_GENERATOR.THICKNESS', { unit: unit === 'ft' ? 'in' : 'cm' })}
              </FieldLabel>
              <Input
                id="rug-thickness"
                inputMode="decimal"
                className="max-w-32"
                value={thickness}
                onChange={(e) => { setThickness(e.target.value); setModel(null); }}
              />
              <FieldDescription className={cn(!thicknessOk && 'text-destructive')} data-testid="rug-thickness-hint">
                {thicknessOk
                  ? t('SUPER_ADMIN.RUG_GENERATOR.THICKNESS_HINT', { cm: Math.round(thicknessCm * 10) / 10 })
                  : t('SUPER_ADMIN.RUG_GENERATOR.THICKNESS_INVALID', { min: THICKNESS_CM.min, max: THICKNESS_CM.max })}
              </FieldDescription>
            </Field>
            <div className="flex flex-wrap gap-1.5">
              {THICKNESS_PRESETS[unit].map((v) => (
                <Button key={v} variant="outline" size="sm" onClick={() => { setThickness(String(v)); setModel(null); }}>
                  {v} {unit === 'ft' ? 'in' : 'cm'}
                </Button>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

          <Button size="lg" onClick={make} disabled={!photo || !sizeOk || !thicknessOk || making}>
            {making ? <Loader2 data-icon="inline-start" className="animate-spin" /> : model ? <RefreshCw data-icon="inline-start" /> : <Box data-icon="inline-start" />}
            {t(making ? 'SUPER_ADMIN.RUG_GENERATOR.MAKING' : model ? 'SUPER_ADMIN.RUG_GENERATOR.REMAKE' : 'SUPER_ADMIN.RUG_GENERATOR.MAKE')}
          </Button>
        </Card>

        {/* The model */}
        <Card className="flex min-h-[420px] flex-col overflow-hidden p-0">
          {model ? (
            <>
              <div className="min-h-[360px] flex-1 bg-muted/40">
                <ModelPreview src={model.url} alt={model.name} testId="rug-preview" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
                <div className="text-sm">
                  <div className="font-medium">{model.name}</div>
                  <div className="text-xs text-muted-foreground">{t('SUPER_ADMIN.RUG_GENERATOR.MODEL_SIZE', { kb: Math.round(model.bytes / 1024) })}</div>
                </div>
                <Button asChild>
                  <a href={model.url} download={model.name} data-testid="rug-download">
                    <Download data-icon="inline-start" />
                    {t('SUPER_ADMIN.RUG_GENERATOR.DOWNLOAD')}
                  </a>
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-sm text-muted-foreground">
              <Box className="size-8" />
              <span>{t('SUPER_ADMIN.RUG_GENERATOR.EMPTY')}</span>
            </div>
          )}
        </Card>
      </div>
  );
};
